import React from 'react';
import { FaEdit, FaCheckCircle, FaGlobe, FaLock, FaGithub, FaNpm } from 'react-icons/fa';
import { SiPypi } from 'react-icons/si';
import { contentTypes } from '../../utils/designTokens';

/**
 * Step 8: Review & Publish
 * - Summary of all data
 * - Publish toggle
 * - Edit buttons for each section
 */
const MCPServerFormStep8 = ({ formData, updateFormData, onEdit }) => {
  const handlePublishToggle = (e) => {
    updateFormData({ isPublished: e.target.checked });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Review & Publish
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Review your MCP server details before publishing.
        </p>
      </div>

      {/* Publish Toggle */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-6">
        <label className="flex items-start gap-4 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isPublished}
            onChange={handlePublishToggle}
            className="mt-1 w-5 h-5"
          />
          <div>
            <div className="font-semibold text-gray-900 dark:text-spooky-ghost-white text-lg flex items-center gap-2">
              {formData.isPublished ? (
                <>
                  <FaGlobe className="text-cyan-500" />
                  Publish to Community
                </>
              ) : (
                <>
                  <FaLock className="text-gray-500" />
                  Save as Draft
                </>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {formData.isPublished
                ? 'Your server will be visible to all users and searchable in the directory'
                : 'Save as a private draft. You can publish it later from your profile.'}
            </p>
          </div>
        </label>
      </div>

      {/* Summary Sections */}
      <div className="space-y-4">
        {/* Basic Info */}
        <SummarySection
          title="Basic Information"
          onEdit={() => onEdit(1)}
          icon={contentTypes.mcp_server.icon}
        >
          <div className="space-y-2">
            <div>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Title:</span>
              <p className="text-gray-900 dark:text-spooky-ghost-white">{formData.title || <em className="text-gray-400">Not set</em>}</p>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Description:</span>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{formData.description || <em className="text-gray-400">Not set</em>}</p>
            </div>
            {formData.tags.length > 0 && (
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Tags:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 rounded text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SummarySection>

        {/* Connection Config */}
        <SummarySection
          title="Connection Configuration"
          onEdit={() => onEdit(2)}
          icon="🔌"
        >
          <div className="bg-gray-50 dark:bg-spooky-bg-light rounded p-3">
            <code className="text-xs font-mono text-gray-800 dark:text-gray-300 whitespace-pre-wrap">
              {JSON.stringify(formData.connectionConfig, null, 2)}
            </code>
          </div>
        </SummarySection>

        {/* Capabilities */}
        <SummarySection
          title="Capabilities & Dependencies"
          onEdit={() => onEdit(3)}
          icon="⚡"
        >
          <div className="space-y-2">
            <div>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">MCP Version:</span>
              <span className="ml-2 text-gray-900 dark:text-spooky-ghost-white">{formData.mcpVersion}</span>
            </div>
            {formData.programmingLanguage && (
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Language:</span>
                <span className="ml-2 text-gray-900 dark:text-spooky-ghost-white capitalize">{formData.programmingLanguage}</span>
              </div>
            )}
            {formData.capabilities.length > 0 && (
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Capabilities:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded text-xs"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SummarySection>

        {/* Compatible Clients */}
        <SummarySection
          title="Compatible Clients"
          onEdit={() => onEdit(4)}
          icon="🖥️"
        >
          {formData.compatibleClients.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {formData.compatibleClients.map((client) => (
                <span
                  key={client}
                  className="px-3 py-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-full text-sm font-semibold"
                >
                  {client}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">No clients specified</p>
          )}
        </SummarySection>

        {/* Environment Variables */}
        <SummarySection
          title="Environment Variables"
          onEdit={() => onEdit(5)}
          icon="🔐"
        >
          {Object.keys(formData.environmentVars).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(formData.environmentVars).map(([name, config]) => (
                <div key={name} className="text-sm">
                  <code className="font-mono text-cyan-600 dark:text-cyan-400">{name}</code>
                  {config.required && (
                    <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs rounded">
                      Required
                    </span>
                  )}
                  {config.description && (
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">{config.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">No environment variables</p>
          )}
        </SummarySection>

        {/* Setup Instructions */}
        <SummarySection
          title="Setup Instructions"
          onEdit={() => onEdit(6)}
          icon="📖"
        >
          <div className="bg-gray-50 dark:bg-spooky-bg-light rounded p-3 max-h-48 overflow-y-auto">
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {formData.setupInstructions || <em className="text-gray-400">No instructions provided</em>}
            </p>
          </div>
        </SummarySection>

        {/* Repository Links */}
        <SummarySection
          title="Repository & Packages"
          onEdit={() => onEdit(7)}
          icon="🔗"
        >
          {(formData.githubUrl || formData.npmPackage || formData.pipPackage) ? (
            <div className="space-y-1">
              {formData.githubUrl && (
                <div className="text-sm flex items-center gap-2">
                  <FaGithub />
                  <a href={formData.githubUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 dark:text-cyan-400 hover:underline">
                    GitHub
                  </a>
                </div>
              )}
              {formData.npmPackage && (
                <div className="text-sm flex items-center gap-2">
                  <FaNpm className="text-red-600" />
                  <span className="text-gray-900 dark:text-spooky-ghost-white">{formData.npmPackage}</span>
                </div>
              )}
              {formData.pipPackage && (
                <div className="text-sm flex items-center gap-2">
                  <SiPypi className="text-blue-600" />
                  <span className="text-gray-900 dark:text-spooky-ghost-white">{formData.pipPackage}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">No repository links</p>
          )}
        </SummarySection>
      </div>

      {/* Ready to Publish */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <FaCheckCircle className="text-green-600 dark:text-green-400 text-2xl flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-green-900 dark:text-green-300 mb-1">
              Ready to {formData.isPublished ? 'Publish' : 'Save'}!
            </h3>
            <p className="text-sm text-green-800 dark:text-green-400">
              {formData.isPublished
                ? 'Click "Publish MCP Server" below to share your server with the community.'
                : 'Click "Publish MCP Server" below to save your draft.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for summary sections
const SummarySection = ({ title, onEdit, icon, children }) => {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-spooky-bg-medium">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 dark:text-spooky-ghost-white flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          {title}
        </h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 text-sm font-medium inline-flex items-center gap-1"
        >
          <FaEdit />
          Edit
        </button>
      </div>
      {children}
    </div>
  );
};

export default MCPServerFormStep8;
